import { DoorOpen, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  longOpenCount,
  useWorkEntries,
  workInsideOf,
  type WorkInside,
} from "@/shared/api/hooks/workEntries";
import { cn } from "@/shared/lib/cn";

/**
 * 관제 「작업 입실 중 N명 (M건)」 (2026-10-27 사용자 결정 「작업 입실 등록」).
 *
 * 현장 PC 「진입 전 확인」의 [작업 입실] 기록 중 아직 퇴실하지 않은 것의 인원 합이다. 시스템은 누가 안에 있는지
 * 모른다(위치 미추적) — 그래서 숫자 옆에 늘 「입실 기록 기준」을 붙이고, 0건이면 「0명」 대신 「입실 기록 없음」이라
 * 쓴다(「안에 아무도 없다」로 읽히지 않게). 경보·훈련 창이 열려 있고 입실 기록이 남아 있으면 강조한다 — 무전으로
 * 먼저 확인할 대상이다. 12시간 넘게 열린 기록은 서버가 닫지 않으므로 「퇴실 미처리 의심」으로 따로 알린다.
 *
 *   · `WorkInsideSummary` — 대피 현황 패널 안의 블록(대피 현황 응답의 `work_inside` 를 받는다).
 *   · `WorkInsideAlarmLine` — 경보 창의 한 줄(현장 id 로 직접 조회 — 경보 창은 대피 현황을 받지 않는다).
 * 옛 서버(`work_inside` 없음)는 부르는 쪽이 그리지 않는다.
 */

/** 패널에 펼쳐 보이는 입실 기록 수 — 나머지는 「외 N건」. */
const LIST_MAX = 4;

export type WorkInsideEmphasis = "alert" | "drill" | null;

export function WorkInsideSummary({ work, emphasis }: { work: WorkInside; emphasis: WorkInsideEmphasis }) {
  const { t } = useTranslation();
  const now = useMinuteClock(work.count > 0);
  const longOpen = longOpenCount(work);

  if (work.count === 0) {
    return (
      <div className="mb-3 flex items-center gap-1.5 rounded-lg bg-[var(--bg)] px-3 py-2 text-[12.5px] text-[var(--fg-subtle)]">
        <DoorOpen size={14} className="shrink-0" />
        <span className="min-w-0 truncate">{t("evac.work.none")}</span>
        <span className="ml-auto shrink-0 text-[11px]" title={t("evac.work.basisNote")}>
          {t("evac.work.basisShort")}
        </span>
      </div>
    );
  }

  const strong = emphasis != null;
  const tone = emphasis === "alert" ? "danger" : "warning";
  // 강조면 등급 면(-soft) 위에 그 짝 글자색(-fg)만 쓴다 — 대비 게이트 규칙(tokens.css 「Hazard PAIRS」).
  const fg = strong ? `var(--${tone}-fg)` : "var(--fg)";
  const sub = strong ? `var(--${tone}-fg)` : "var(--fg-subtle)";
  return (
    <section
      aria-label={t("evac.work.title")}
      className="mb-3 rounded-lg border px-3 py-2.5"
      style={
        strong
          ? { background: `var(--${tone}-soft)`, borderColor: `var(--${tone}-line)` }
          : { background: "var(--bg)", borderColor: "var(--border)" }
      }
    >
      {/* 좁은 칸·긴 언어에서는 줄을 바꾼다 — 「입실 기록 기준」이 테두리 밖으로 밀려나지 않게. */}
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold" style={{ color: fg }}>
          <DoorOpen size={16} className="shrink-0" style={{ color: strong ? `var(--${tone}-line)` : "var(--fg-subtle)" }} />
          {t("evac.work.title")}
        </span>
        <span className="inline-flex items-baseline gap-1 whitespace-nowrap">
          <span className="text-[20px] font-bold leading-none tabular-nums" style={{ color: fg }}>
            {t("evac.work.people", { n: work.headcount })}
          </span>
          <span className="text-[12px] tabular-nums" style={{ color: sub }}>
            {t("evac.work.countParen", { n: work.count })}
          </span>
        </span>
        <span className="ml-auto text-[11px]" style={{ color: sub }} title={t("evac.work.basisNote")}>
          {t("evac.work.basisShort")}
        </span>
      </div>

      {strong && (
        <p className="mt-1.5 text-[12.5px] font-medium leading-snug" style={{ color: fg }}>
          {emphasis === "alert" ? t("evac.work.activeAlert") : t("evac.work.activeDrill")}
        </p>
      )}

      {longOpen > 0 && (
        <p
          className="mt-1.5 flex items-start gap-1.5 rounded-md bg-[var(--warning-soft)] px-2 py-1.5 text-[12px] leading-snug text-[var(--warning-fg)]"
        >
          <TriangleAlert size={13} className="mt-[1px] shrink-0" />
          {t("evac.work.longOpenCount", { n: longOpen })}
        </p>
      )}

      <ul className="mt-2 space-y-1">
        {work.entries.slice(0, LIST_MAX).map((e) => (
          <li key={e.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px]" style={{ color: fg }}>
            <span className="font-semibold tabular-nums">{t("evac.work.people", { n: e.headcount })}</span>
            <span className="tabular-nums" style={{ color: sub }}>
              {t("evac.work.enteredAt", { time: entryClock(e.entered_at) })} · {elapsedText(t, e.entered_at, now)}
            </span>
            {e.note && <span className="min-w-0 truncate" style={{ color: sub }}>· {e.note}</span>}
            {e.long_open && <LongOpenTag />}
          </li>
        ))}
      </ul>
      {work.entries.length > LIST_MAX && (
        <p className="mt-1 text-[11.5px]" style={{ color: sub }}>
          {t("evac.work.more", { n: work.entries.length - LIST_MAX })}
        </p>
      )}
      <p className="mt-2 text-[11px] leading-snug" style={{ color: sub }}>
        {t("evac.work.basisNote")}
      </p>
    </section>
  );
}

/**
 * 경보 창의 한 줄 — 고른 경보의 현장에 입실 기록이 남아 있는가. 조회가 안 되면(옛 서버·접근 불가) 그리지 않는다.
 * 대피 현황은 관제 역할 전용이라 여기서는 현장 접근만 있으면 되는 입실 목록 API 를 쓴다(소켓으로 바로 바뀐다).
 */
export function WorkInsideAlarmLine({ siteId }: { siteId: number | null | undefined }) {
  const { t } = useTranslation();
  const q = useWorkEntries(siteId ?? undefined);
  const work = workInsideOf(q.data);
  if (!work) return null;
  const longOpen = longOpenCount(work);
  if (work.count === 0) {
    return (
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12.5px] text-[var(--fg-subtle)]">
        <DoorOpen size={14} className="shrink-0" />
        <span>{t("evac.work.none")}</span>
        <span className="text-[11.5px]">· {t("evac.work.basisShort")}</span>
      </div>
    );
  }
  return (
    <div
      role="status"
      className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md bg-[var(--danger-soft)] px-2.5 py-1.5 text-[12.5px] text-[var(--danger-fg)]"
    >
      <DoorOpen size={14} className="shrink-0" />
      <span className="font-semibold tabular-nums">
        {t("evac.work.summary", { n: work.headcount, count: work.count })}
      </span>
      <span className="text-[11.5px]">· {t("evac.work.basisShort")}</span>
      {longOpen > 0 && (
        <span className="inline-flex items-center gap-1 text-[11.5px]">
          · <TriangleAlert size={12} className="shrink-0" />
          {t("evac.work.longOpenShort", { n: longOpen })}
        </span>
      )}
    </div>
  );
}

/** 「퇴실 미처리 의심」 꼬리표 — 12시간 넘게 열린 입실. */
export function LongOpenTag({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span
      title={t("evac.work.longOpenHint")}
      className={cn(
        "inline-flex shrink-0 items-center gap-1 rounded bg-[var(--warning-soft)] px-1.5 py-[1px] text-[11px] font-medium text-[var(--warning-fg)]",
        className,
      )}
    >
      <TriangleAlert size={11} className="shrink-0" />
      {t("evac.work.longOpen")}
    </span>
  );
}

/* ─── helpers ─────────────────────────────────────────────────────────── */

/** 입실 시각 — 오늘이면 「14:02」, 다른 날이면 날짜를 붙인다(어제 들어간 기록을 오늘 것으로 읽지 않게). */
export function entryClock(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "—";
  const hm: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
  const sameDay = d.toDateString() === new Date().toDateString();
  return d.toLocaleString(undefined, sameDay ? hm : { month: "numeric", day: "numeric", ...hm });
}

/** 입실 뒤 지난 시간 — 「2시간 5분째」·「12분째」. 기기 시계가 서버보다 늦어 음수면 0분. */
export function elapsedText(t: TFunction, iso: string, now: number): string {
  const ms = now - new Date(iso).getTime();
  const min = Number.isFinite(ms) ? Math.max(0, Math.floor(ms / 60_000)) : 0;
  const h = Math.floor(min / 60);
  return h > 0 ? t("evac.work.elapsedHM", { h, m: min % 60 }) : t("evac.work.elapsedM", { m: min });
}

/** 1분마다 바뀌는 시계 — 「N분째」를 다시 그린다. 입실 기록이 있을 때만 돈다. */
export function useMinuteClock(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(id);
  }, [on]);
  return now;
}
